import type { WunderbricksStatusDto } from "@/api/types";
import wunderbricksPng from "@/assets/images/integrations/wunderbricks.png";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { BrandMark } from "@/design-system/BrandMark";
import { Button } from "@/design-system/Button";
import { Card, CardCenteredState } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTranslation } from "@/translations";

type TicketWunderbricksCardProps = {
  ticketId: string;
  wunderbricks: WunderbricksStatusDto;
};

export function TicketWunderbricksCard({ ticketId, wunderbricks }: TicketWunderbricksCardProps): React.ReactNode {
  const { t } = useTranslation();
  const sendTicketMutation = ticketMutations.useSendTicketToWunderbricks(ticketId);

  return (
    <Card>
      <Inline justify="center">
        <BrandMark src={wunderbricksPng} alt="Wunderbricks logo" />
      </Inline>
      {sendTicketMutation.isPending ? (
        <CardCenteredState>
          <Spinner size="sm" />
        </CardCenteredState>
      ) : (
        <Stack gap="md" align="center">
          {wunderbricks.state === "notStarted" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.wunderbricks.state")} {t("page.tickets.wunderbricks.state.not-started")}
            </Text>
          )}
          {wunderbricks.state === "failed" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.wunderbricks.state")}{" "}
              <Text variant="body-spacious" tone="danger-secondary" overflow="normal">
                {wunderbricks.errorDetails || t("page.tickets.wunderbricks.state.error")}
              </Text>
            </Text>
          )}
          {wunderbricks.state === "cancelled" && (
            <Text variant="body-spacious" tone="secondary" overflow="normal" data-testid="ticket-integration-status">
              {t("page.tickets.wunderbricks.state")}{" "}
              <Text variant="body-spacious" tone="danger-secondary" overflow="normal">
                {wunderbricks.errorDetails || t("page.tickets.wunderbricks.state.cancelled")}
              </Text>
            </Text>
          )}
          {wunderbricks.state === "sent" && (
            <Stack gap="sm" align="center" data-testid="ticket-integration-status">
              <Text as="div" variant="body-emphasis" tone="success" align="center">
                <Inline gap="xs" align="center" wrap={false}>
                  <CheckIcon size={16} />
                  {t("page.tickets.wunderbricks.state.success")}
                </Inline>
              </Text>
              {wunderbricks.caseNumber && (
                <Text variant="caption" tone="secondary">
                  {t("page.tickets.wunderbricks.case-number")} {wunderbricks.caseNumber}
                </Text>
              )}
              {wunderbricks.status && (
                <Text variant="caption" tone="secondary">
                  {t("page.tickets.wunderbricks.status")} {wunderbricks.status}
                </Text>
              )}
            </Stack>
          )}

          {!wunderbricks.isClosed && (
            <Button onClick={() => sendTicketMutation.mutate()} disabled={!wunderbricks.canSubmit}>
              {t("page.tickets.wunderbricks.actions.send-ticket")}
            </Button>
          )}
        </Stack>
      )}
    </Card>
  );
}
